import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Layers, Loader2, ArrowLeft } from 'lucide-react';
import { GoogleLogin } from '@react-oauth/google';
import { authAPI, userAPI } from '../services/api';
import useUserStore from '../store/userStore';
import { generateFingerprint } from '../utils/fingerprint';
import { trackLogin, trackSignUp, setUserId } from '../utils/analytics';

const LoginPage = () => {
  const navigate = useNavigate();
  const { setUser } = useUserStore();
  const [isLoading, setIsLoading] = useState(false);

  const handleGoogleSuccess = async (credentialResponse) => {
    setIsLoading(true);
    try {
      const response = await authAPI.googleAuth(credentialResponse.credential);
      const { user, token, is_new_user } = response.data;
      setUser(user, token);

      // Track analytics
      setUserId(String(user.id));
      if (is_new_user) {
        trackSignUp('google');
      } else {
        trackLogin('google');
      }

      // Try to claim any anonymous submissions from before login
      try {
        const fingerprint = await generateFingerprint();
        const claimResponse = await userAPI.claimAnonymousSubmissions(fingerprint);
        if (claimResponse.data?.claimed_count > 0) {
          toast.success(`Found ${claimResponse.data.claimed_count} previous submission(s)! They've been added to your account.`);
        }
      } catch (claimError) {
        // Silently ignore claim errors - not critical
        console.log('No anonymous submissions to claim:', claimError);
      }

      if (is_new_user) {
        toast.success(`Welcome to buildex.dev, ${user.username}!`);
      } else {
        toast.success(`Welcome back, ${user.username}!`);
      }

      navigate('/');
    } catch (error) {
      console.error('Google auth error:', error);
      const message = error.response?.data?.error || 'Google login failed. Please try again.';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleError = () => {
    toast.error('Google login failed. Please try again.');
  };

  return (
    <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Back to Home */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        {/* Header */}
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg shadow-purple-500/25">
              <Layers className="w-9 h-9 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-white">
            Welcome Back
          </h1>
          <p className="mt-2 text-slate-400">
            Sign in to continue your system design journey
          </p>
        </div>

        {/* Sign In Card */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8 backdrop-blur-sm">
          {/* Google Sign In */}
          <div className="flex flex-col items-center">
            {isLoading ? (
              <div className="flex items-center gap-2 py-3 text-slate-400">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Signing you in...</span>
              </div>
            ) : (
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                theme="filled_black"
                size="large"
                width="100%"
                text="signin_with"
                shape="rectangular"
              />
            )}
          </div>

          {/* Register Link */}
          <p className="mt-6 text-center text-sm text-slate-400">
            Don't have an account?{' '}
            <Link to="/register" className="text-blue-400 hover:text-blue-300 font-medium">
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
